/**
 * LedgerExplorer — ledger list on the left, the selected ledger's complete
 * Tally ledger view on the right.
 *
 * The right pane now renders LedgerStatementView, fed by
 * GET /db/companies/:id/ledger-statement. That replaces an earlier ad-hoc
 * read of /ledger-entries which could not produce a correct statement:
 *
 *   • it displayed ledger_entries.balance as the running balance, and that
 *     column is NULL for all 134,158 rows in the database — so the Balance
 *     column was ₹0.00 on every row;
 *   • it had no Particulars (contra ledger) column, which is the column Tally
 *     leads with;
 *   • opening/closing came from the ledger master, which is a whole-sync
 *     snapshot and does not move with the date range;
 *   • the Closing Balance card read summary.closingBalance, which was never
 *     set (the field is named `closing`), so it always rendered ₹0.00.
 *
 * The statement endpoint computes all of it from the same ledger_entries rows
 * the Trial Balance is derived from, so this pane and the Trial Balance agree,
 * and every row drills through to the voucher that produced it.
 */

import React, { useCallback, useMemo, useState } from "react";
import "./LedgerExplorer.css";
import LedgerStatementView from "./LedgerStatementView";
import DrillDownModal from "./DrillDownModal";
import { useLedgerStatement } from "../hooks/useDashboard";
import useDrillDown from "../hooks/useDrillDown";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Math.abs(Number(amount) || 0));

const getCurrentFY = () => {
  const today = new Date();
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return { from: `${year}-04-01`, to: `${year + 1}-03-31` };
};

const LedgerExplorer = ({ ledgers = [], showCompany = false, onLedgerSelect }) => {
  const fy = useMemo(getCurrentFY, []);

  const [search, setSearch] = useState("");
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [fromDate, setFromDate] = useState(fy.from);
  const [toDate, setToDate] = useState(fy.to);

  const drill = useDrillDown();

  const filteredLedgers = useMemo(() => {
    const q = search.toLowerCase();
    return ledgers.filter((l) => l.name?.toLowerCase().includes(q));
  }, [ledgers, search]);

  // The statement is fetched server-side for the chosen window, so opening and
  // closing move with the dates instead of staying pinned to the sync snapshot.
  const { data: statement, loading, loadingAll, error, loadAll, nextPage, prevPage } = useLedgerStatement(
    selectedLedger?.companyId,
    {
      ledgerGuid: selectedLedger?.guid,
      ledgerName: selectedLedger?.guid ? undefined : selectedLedger?.name,
      from: fromDate || undefined,
      to: toDate || undefined,
    }
  );

  const selectLedger = useCallback((ledger) => {
    setSelectedLedger(ledger);
    if (onLedgerSelect) onLedgerSelect(ledger);
  }, [onLedgerSelect]);

  const handleOpenVoucher = useCallback((row) => {
    if (!row?.voucherGuid || !selectedLedger?.companyId) return;
    drill.openVoucher({
      companyId: selectedLedger.companyId,
      guid: row.voucherGuid,
      label: row.voucherNumber || "Voucher",
    });
  }, [drill, selectedLedger]);

  return (
    <div className="ledger-explorer">
      <div className="ledger-sidebar">
        <input
          className="ledger-search"
          placeholder="Search Ledger..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="ledger-list">
          {filteredLedgers.map((ledger) => (
            <div
              key={ledger.id}
              className={`ledger-item ${selectedLedger?.id === ledger.id ? "active" : ""}`}
              onClick={() => selectLedger(ledger)}
            >
              <div className="ledger-name">{ledger.name}</div>
              <div className="ledger-group">{ledger.Group?.name}</div>
              <div className="ledger-balance">{formatCurrency(ledger.closingBalance)}</div>
              {showCompany && <div className="ledger-company">{ledger.companyName}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="ledger-content">
        {selectedLedger ? (
          <>
            <div className="ledger-header">
              <h2>{selectedLedger.name}</h2>
              <span className="ledger-tag">{selectedLedger.Group?.name}</span>
            </div>

            <div className="ledger-entry-toolbar">
              <label>
                From{' '}
                <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                To{' '}
                <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              <button
                type="button"
                className="dd-btn"
                onClick={() => { setFromDate(''); setToDate(''); }}
              >
                All dates
              </button>
            </div>

            <LedgerStatementView
              data={statement}
              loading={loading}
              loadingAll={loadingAll}
              error={error}
              onOpenVoucher={handleOpenVoucher}
              onLoadAll={loadAll}
              onNextPage={nextPage}
              onPrevPage={prevPage}
            />
          </>
        ) : (
          <div className="ledger-empty">
            <h2>Select a Ledger</h2>
            <p>Choose a ledger from the left panel to view all its transactions.</p>
          </div>
        )}
      </div>

      {drill.isOpen && (
        <DrillDownModal
          stack={drill.stack}
          onPush={drill.push}
          onPopTo={drill.popTo}
          onClose={drill.close}
        />
      )}
    </div>
  );
};

export default LedgerExplorer;
